import React, { useEffect, useState } from "react"
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Skeleton, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { filterType } from "../../../redux-store/types/api-types";
import CustomDropdown from "../../../lib/formik/CustomDropdown";
import { formatCurrency } from "../../../utils/utilityFunctions";
import InfoToltip from "../components/InfoToltip";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";
import { getChartA11yOptions } from "../../../utils/highchartsAccessibility";

interface Props {
    data: {
        name: Array<string>;
        value: Array<number>;
    };
    filterOption?: filterType[];
    isLoading: boolean;
    setFilterValue?: React.Dispatch<React.SetStateAction<string | number>>
    error?: boolean
    aumAmount?: number
}

export function LineChartComponent({
    isLoading,
    error,
    data,
    aumAmount,
    showTooltip = true,
    showCurrency = true
}: {
    isLoading: boolean;
    error?: boolean;
    data: {
        name: Array<string>;
        value: Array<number>;
    };
    aumAmount?: number;
    showTooltip?: boolean;
    showCurrency?: boolean
}): JSX.Element {
    const theme = useTheme()

    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const reducerValue = data?.value.reduce((a: number, b: number) => a + b, 0)

    const a11y = getChartA11yOptions({
        altText: 'AUM distribution bar chart',
        description: 'Horizontal bar chart showing AUM distribution across the top investors for the selected period.',
    });

    const option = {
        chart: {
            type: 'bar',
            backgroundColor: theme.palette.customColors.infoCard,
            style: {
                fontFamily: theme.typography.fontFamily,
            },
            description: a11y.chartDescription,
        },
        title: a11y.title,
        accessibility: a11y.accessibility,
        xAxis: {
            categories: data?.name,
            title: {
                text: null
            },
            gridLineWidth: 1,
            lineWidth: 0,
            gridLineColor: '#D9E3FF80',
            gridLineDashStyle: 'dash',
            labels: {
                align: 'left',
                reserveSpace: true,
                formatter: function (this: any) {
                    const { value } = this;
                    const text = `${value ?? ""}`?.toLowerCase();

                    return `<div 
                    title="${text?.toUpperCase()}" 
                    style="
                      text-overflow: ellipsis;
                      overflow: hidden;
                      white-space: nowrap;
                      max-width: 150px;
                      display: block;
                    ">${text}</div>`;
                },
                style: {
                    fontSize: '15px',
                    textTransform: "capitalize",
                    maxWidth: '200px',
                    width: "100%",
                    color: theme.palette.text.primary
                },
                useHTML: true,
            },
        },

        tooltip: {
            enabled: showTooltip,
            useHTML: true,
            formatter: function () {

                const { y } = this

                return ` 
               <div 
                    style="
                        display: flex;
                        justify-content: center;
                        flex-direction: column;
                        width: 200px;
                        height: 60px;
                        background-color: ${theme.palette.customColors.cinder}; 
                        border-radius: 0px; 
                        border-width: 1px;
                        padding: 10px;
                        "
                >
                    <div style="display: flex; justify-content: space-between;">
                        <p style="font-weight: 400;font-size: 14px;letter-spacing:0.02px;color: #FFFFFF;">Market Value</p>
                        <p style="font-weight: 400;font-size: 14px;letter-spacing:0.02px;color: #FFFFFF;">% of book</p>
                    </div>
                    <div  style="display: flex; justify-content: space-between;margin-top:-20px">
                        <p style="color: #FFFFFF;font-size: 16px;letter-spacing:0.02px">
                            ${showCurrency ? formatCurrency({ value: y, locale, currencyCode }) : y}
                        </p>
                        <p style="color: #FFFFFF;font-size: 16px;letter-spacing:0.02px">
                            ${((y / Number(aumAmount)) * 100)?.toFixed(2)}%
                        </p>
                    </div>
                </div> 
                `
            },
            backgroundColor: '#0F131F',
            opacity: 0.1,
            hovered: true,
            borderWidth: 0,
            borderRadius: 20,
            shadow: false,
            shape: "square",
            followPointer: true,
        } as any,
        yAxis: {
            min: 0,
            title: {
                text: '',
            },
            labels: {
                useHTML: true,
                rotation: -45,
                formatter: function (this: any) {
                    const { value } = this
                    return showCurrency ? formatCurrency({ value, locale, currencyCode }) : `${value}`
                } as any,
                style: {
                    fontSize: '14px',
                    textOverflow: 'none',
                    whiteSpace: 'nowrap',
                    gap: '3px',
                    color: theme.palette.text.primary

                }
            },
            gridLineWidth: 0,

        },
        Animation: false,
        plotOptions: {
            bar: {
                borderRadius: 0,
                dataLabels: {
                    enabled: true
                },
                groupPadding: 0.1,
                pointRange: .5,
            },
        },
        legend: {
            layout: 'vertical',
            align: 'right',
            verticalAlign: 'top',
            x: -40,
            y: 80,
            // floating: false,
            borderWidth: 1,

        },
        credits: {
            enabled: false
        },
        series: [{
            // name: 'AUM',
            data: data?.value,
            color: '#1E90FF',
            showInLegend: false,
            borderRadius: 8,
            borderWidth: 0,
            pointWidth: 10,
            pointPadding: 0,
            dataLabels: {
                enabled: false
            },
            states: {
                // hover: {
                //     color: '#1E90FF',
                //     borderColor: '#1E90FF',
                //     opacity: .8
                // }
            },
        }],
    }

    return (
        isLoading ?
            <SkeletonChart /> :
            error ?
                <ErrorText />
                :
                (data?.name?.length || 0) > 0 &&
                    reducerValue > 0 ? <HighchartsReact
                    highcharts={Highcharts}
                    options={{ ...option }}
                    containerProps={{ style: { height: 350, width: '100%', padding: 0, margin: 0 } }}
                    background="red" /> : <ErrorText />

    )
}


export default function CustomeLineChart(props: Props): JSX.Element {
    const theme = useTheme()

    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const totalAUM = props?.data?.value.reduce((a: number, b: number) => a + b, 0);

    // const option = {
    //     chart: {
    //         type: 'bar',
    //         backgroundColor: theme.palette.customColors.infoCard,
    //         style: {
    //             fontFamily: theme.typography.fontFamily,
    //         },
    //     },
    //     title: {
    //         text: '',
    //         align: 'left'
    //     },
    //     xAxis: {
    //         categories: props?.data?.name,
    //         title: {
    //             text: null
    //         },
    //         gridLineWidth: 1,
    //         lineWidth: 0,
    //         gridLineColor: '#D9E3FF80',
    //         gridLineDashStyle: 'dash',
    //         labels: {
    //             align: 'left',
    //             reserveSpace: true,
    //             formatter: function (this: any) {
    //                 const { value } = this;
    //                 const text = `${value ?? ""}`?.toLowerCase();

    //                 return `<div 
    //                 title="${text?.toUpperCase()}" 
    //                 style="
    //                   text-overflow: ellipsis;
    //                   overflow: hidden;
    //                   white-space: nowrap;
    //                   max-width: 150px;
    //                   display: block;
    //                 ">${text}</div>`;
    //             },
    //             style: {
    //                 fontSize: '15px',
    //                 textTransform: "capitalize",
    //                 maxWidth: '200px',
    //                 width: "100%",
    //                 color: theme.palette.text.primary
    //             },
    //             useHTML: true,
    //         },
    //     },

    //     tooltip: {
    //         useHTML: true,
    //         formatter: function () {

    //             const { y } = this

    //             return ` 
    //            <div 
    //                 style="
    //                     display: flex;
    //                     justify-content: center;
    //                     flex-direction: column;
    //                     width: 200px;
    //                     height: 60px;
    //                     background-color: ${theme.palette.customColors.cinder}; 
    //                     border-radius: 0px; 
    //                     border-width: 1px;
    //                     padding: 10px;
    //                     "
    //             >
    //                 <div style="display: flex; justify-content: space-between;">
    //                     <p style="font-weight: 400;font-size: 14px;letter-spacing:0.02px;color: #FFFFFF;">Market Value</p>
    //                     <p style="font-weight: 400;font-size: 14px;letter-spacing:0.02px;color: #FFFFFF;">% of book</p>
    //                 </div>
    //                 <div  style="display: flex; justify-content: space-between;margin-top:-20px">
    //                     <p style="color: #FFFFFF;font-size: 16px;letter-spacing:0.02px">
    //                         ${formatCurrency({ value: y, locale, currencyCode })}
    //                     </p>
    //                     <p style="color: #FFFFFF;font-size: 16px;letter-spacing:0.02px">
    //                         ${((y / Number(props?.aumAmount)) * 100)?.toFixed(2)}%
    //                     </p>
    //                 </div>
    //             </div> 
    //             `
    //         },
    //         backgroundColor: '#0F131F',
    //         opacity: 0.1,
    //         hovered: true,
    //         borderWidth: 0,
    //         borderRadius: 20,
    //         shadow: false,
    //         shape: "square",
    //         followPointer: true,
    //     } as any,
    //     yAxis: {
    //         min: 0,
    //         title: {
    //             text: '',
    //         },
    //         labels: {
    //             useHTML: true,
    //             rotation: -45,
    //             formatter: function (this: any) {
    //                 const { value } = this
    //                 return formatCurrency({ value, locale, currencyCode })
    //             } as any,
    //             style: {
    //                 fontSize: '14px',
    //                 textOverflow: 'none',
    //                 whiteSpace: 'nowrap',
    //                 gap: '3px',
    //                 color: theme.palette.text.primary

    //             }
    //         },
    //         gridLineWidth: 0,

    //     },
    //     Animation: false,
    //     plotOptions: {
    //         bar: {
    //             borderRadius: 0,
    //             dataLabels: {
    //                 enabled: true
    //             },
    //             groupPadding: 0.1,
    //             pointRange: .5,
    //         },
    //     },
    //     legend: {
    //         layout: 'vertical',
    //         align: 'right',
    //         verticalAlign: 'top',
    //         x: -40,
    //         y: 80,
    //         // floating: false,
    //         borderWidth: 1,

    //     },
    //     credits: {
    //         enabled: false
    //     },
    //     series: [{
    //         // name: 'AUM',
    //         data: props?.data?.value,
    //         color: '#1E90FF',
    //         showInLegend: false,
    //         borderRadius: 8,
    //         borderWidth: 0,
    //         pointWidth: 10,
    //         pointPadding: 0,
    //         dataLabels: {
    //             enabled: false
    //         },
    //         states: {
    //             // hover: {
    //             //     color: '#1E90FF',
    //             //     borderColor: '#1E90FF',
    //             //     opacity: .8
    //             // }
    //         },
    //     }],
    // }

    const reducerValue = props?.data?.value.reduce((a: number, b: number) => a + b, 0)


    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            // justifyContent: 'space-between',
            padding: "25px 30px 45px 30px",

        }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    paddingBottom: '40px',
                }}
            >
                <Typography
                    variant="h2"
                >
                    AUM Distribution
                    <InfoToltip desc={"AUM distribution of Top 5 Investors"} />
                </Typography>
                {/* {props?.data?.name?.length !== 0 ? */}
                <CustomDropdown
                    setFilterValue={props.setFilterValue}
                    IsLoading={props.isLoading}
                    options={props.filterOption as filterType[]} defaultValue={props?.filterOption?.[0]?.value as number}
                />
                {/* : null
                } */}
            </Box>
            <Box
                component={'div'}
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                }}
            >
                {/* {props?.isLoading ?
                    <SkeletonChart /> :
                    props.error ?
                        <ErrorText />
                        :
                        (props?.data?.name?.length || 0) > 0 &&
                            reducerValue > 0 ? <HighchartsReact
                            highcharts={Highcharts}
                            options={{ ...option }}
                            containerProps={{ style: { height: 350, width: '100%', padding: 0, margin: 0 } }}
                            background="red" /> : <ErrorText />
                } */}
                <LineChartComponent
                    isLoading={props.isLoading}
                    error={props.error}
                    data={props.data}
                    aumAmount={props.aumAmount}
                />
            </Box>
        </Box>
    )
}

const SkeletonChart = () => {
    return (
        <>
            <Box component={'div'} style={{ width: '100%', marginTop: 15 }}>
                <Box component={'div'} style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: 14,
                    alignContent: 'center',
                    padding: 10
                }}>
                    <Box component={'div'} style={{ display: 'flex', flexDirection: 'column', gap: 17 }}>
                        {[...Array(6)].map((val, i) => (
                            <Skeleton key={i} animation={'wave'} width={100} />
                        ))}
                    </Box>
                    <Box component={'div'} style={{ display: 'flex', flexDirection: 'column', gap: 18, width: '100%' }}>
                        {[...Array(6)].map((val, i) => (
                            <Skeleton key={i} animation={'wave'} width={`${Math.floor(Math.random() * 100)}%`} />
                        ))}
                    </Box>
                </Box>
                <Box component={'div'} style={{
                    display: 'flex',
                    flexDirection: 'row',
                    // background: 'red',
                    marginTop: 10,
                    justifyContent: 'space-between',
                    gap: 40,
                    paddingLeft: 100,
                    paddingRight: 30,
                }}>
                    {[...Array(5)].map((val, i) => (
                        <Skeleton key={i} animation={'wave'} width={20} />
                    ))}
                </Box>

            </Box>
        </>
    )
}

export const ErrorText = () => (
    <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        // background: 'red'
    }}>
        <Typography sx={{ textAlign: 'center', }} variant='h2' color={'text.primary'}>No data found</Typography>
    </Box>
)